type SyncTrackedRecord = {
  remoteSyncConflictAt?: string
  remoteSyncFailedAt?: string
  remoteSyncedAt?: string
}

export type RemoteSyncState = "conflict" | "failed" | "local" | "synced"
export const AUTO_SYNC_RETRY_COOLDOWN_MS = 30_000

export function getRemoteSyncState(record: SyncTrackedRecord): RemoteSyncState {
  if (record.remoteSyncedAt) return "synced"
  if (record.remoteSyncConflictAt) return "conflict"
  if (record.remoteSyncFailedAt) return "failed"
  return "local"
}

export function markRecordSyncConflict<T extends SyncTrackedRecord>(
  record: T,
  conflictedAt = new Date().toISOString(),
): T {
  return {
    ...record,
    remoteSyncConflictAt: conflictedAt,
    remoteSyncFailedAt: undefined,
    remoteSyncedAt: undefined,
  }
}

export function markRecordSyncFailed<T extends SyncTrackedRecord>(
  record: T,
  failedAt = new Date().toISOString(),
): T {
  return {
    ...record,
    remoteSyncConflictAt: undefined,
    remoteSyncFailedAt: failedAt,
    remoteSyncedAt: undefined,
  }
}

export function markRecordSyncPending<T extends SyncTrackedRecord>(
  record: T,
): T {
  return {
    ...record,
    remoteSyncConflictAt: undefined,
    remoteSyncFailedAt: undefined,
    remoteSyncedAt: undefined,
  }
}

export function markRecordSyncSucceeded<T extends SyncTrackedRecord>(
  record: T,
): T {
  return {
    ...record,
    remoteSyncConflictAt: undefined,
    remoteSyncFailedAt: undefined,
  }
}

export function shouldAutoRetrySync(
  record: SyncTrackedRecord,
  now = Date.now(),
  cooldownMs = AUTO_SYNC_RETRY_COOLDOWN_MS,
): boolean {
  const state = getRemoteSyncState(record)
  if (state === "synced" || state === "conflict") {
    return false
  }

  if (state === "local") {
    return true
  }

  if (!record.remoteSyncFailedAt) {
    return false
  }

  const failedAt = Date.parse(record.remoteSyncFailedAt)
  if (Number.isNaN(failedAt)) {
    return true
  }

  return now - failedAt >= cooldownMs
}
